'use es6';

import I18n from 'I18n';
import PropTypes from 'prop-types';
import emptyFunction from 'react-utils/emptyFunction';
import classNames from 'classnames';
import { PureComponent } from 'react';
import UIModal from 'UIComponents/dialog/UIModal';
import UIModalPanel from 'UIComponents/dialog/UIModalPanel';
import UIPanel from 'UIComponents/panel/UIPanel';
import UIButton from 'UIComponents/button/UIButton';
import UIList from 'UIComponents/list/UIList';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogHeaderImage from 'UIComponents/dialog/UIDialogHeaderImage';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIImage from 'UIComponents/image/UIImage';
import UIMedia from 'UIComponents/layout/UIMedia';
import UIMediaBody from 'UIComponents/layout/UIMediaBody';
import UIMediaRight from 'UIComponents/layout/UIMediaRight';
import UIPanelHeader from 'UIComponents/panel/UIPanelHeader';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UIPanelFooter from 'UIComponents/panel/UIPanelFooter';
import PromptablePropInterface from 'UIComponents/decorators/PromptablePropInterface';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const propTypes = Object.assign({
  allowHeader: PropTypes.bool.isRequired,
  bodyClassName: PropTypes.string,
  cancelHref: PropTypes.string,
  cancelTarget: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  confirmDisabled: PropTypes.bool,
  confirmDisabledTooltip: PropTypes.node,
  confirmHref: PropTypes.string,
  confirmLabel: PropTypes.node,
  confirmTarget: PropTypes.string.isRequired,
  confirmUse: UIButton.propTypes.use,
  'data-selenium-test': PropTypes.string,
  dialogClassName: PropTypes.string,
  extraFooter: PropTypes.node,
  extraRightAlignedFooter: PropTypes.node,
  footerAlign: UIDialogFooter.propTypes.align,
  headerImage: PropTypes.string,
  headerImageOffsetBottom: PropTypes.number,
  headerImageOffsetTop: PropTypes.number,
  includeFooter: PropTypes.bool,
  isConfirmButtonLoading: PropTypes.bool,
  confirmProps: PropTypes.object,
  moreButtons: PropTypes.node,
  offsetTop: PropTypes.number,
  onCloseComplete: PropTypes.func,
  onConfirm: PropTypes.func,
  onOpenComplete: PropTypes.func,
  onReject: PropTypes.func.isRequired,
  onScroll: PropTypes.func,
  panelClassName: PropTypes.string,
  rejectLabel: PropTypes.node,
  showCancelButton: PropTypes.bool.isRequired,
  showCloseButton: PropTypes.bool.isRequired,
  showConfirmButton: PropTypes.bool.isRequired,
  showExtraFooterFirst: PropTypes.bool,
  size: PropTypes.oneOf(['auto', 'medium', 'short', 'tall']),
  title: PropTypes.node,
  use: PropTypes.oneOfType([UIModal.propTypes.use, PropTypes.oneOf(['embedded', 'panel', 'sidebar'])]),
  width: PropTypes.number.isRequired
}, PromptablePropInterface);
const defaultProps = {
  allowHeader: true,
  cancelTarget: '_self',
  confirmTarget: '_self',
  confirmUse: 'primary',
  footerAlign: 'left',
  includeFooter: true,
  isConfirmButtonLoading: false,
  onConfirm: emptyFunction,
  onReject: emptyFunction,
  onCloseComplete: emptyFunction,
  showCancelButton: true,
  showCloseButton: true,
  showConfirmButton: true,
  showExtraFooterFirst: false,
  size: 'auto',
  use: 'default',
  width: 500
};
const SIDEBAR_BUTTON_STYLE = {
  float: 'right'
};
const EmbeddedComponent = ({
  children
}) => /*#__PURE__*/_jsx("div", {
  style: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  children: children
});
EmbeddedComponent.propTypes = {
  children: PropTypes.node
};
class BaseDialog extends PureComponent {
  getTitleNode() {
    let titleNode = this.props.title;
    if (typeof titleNode === 'string') {
      titleNode = /*#__PURE__*/_jsx("h3", {
        className: "m-all-0",
        children: titleNode
      }, "title-node");
    }
    return titleNode;
  }
  renderHeader() {
    let closeButton;
    const {
      showCloseButton,
      onReject,
      allowHeader,
      use
    } = this.props;
    const titleNode = this.getTitleNode();
    const headerClasses = use === 'embedded' ? 'flex-shrink-0' : "";
    if (!showCloseButton && !titleNode) {
      return null;
    }

    // Most modals should have UIDialogCloseButton,
    // but we'll still include an escape hatch
    // for certain situations
    if (showCloseButton) {
      closeButton = /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onReject,
        "data-selenium-test": "base-dialog-close-btn"
      }, "close-button");
    }
    if (!allowHeader) {
      return [titleNode, closeButton];
    }
    let Component = UIDialogHeader;
    if (use === 'panel') {
      Component = UIPanelHeader;
    }
    return /*#__PURE__*/_jsxs(Component, {
      className: headerClasses,
      children: [titleNode, closeButton]
    });
  }
  renderBody() {
    const {
      children,
      bodyClassName,
      use
    } = this.props;
    const isEmbedded = use === 'embedded';
    const bodyClasses = classNames(bodyClassName, isEmbedded && "overflow-x-hidden overflow-y-auto p-bottom-0");
    if (!children) {
      return null;
    }
    if (use === 'panel') {
      return /*#__PURE__*/_jsx(UIPanelBody, {
        children: /*#__PURE__*/_jsx(UIPanelSection, {
          children: children
        })
      });
    }
    return /*#__PURE__*/_jsxs(UIDialogBody, {
      className: bodyClasses,
      children: [this.renderHeaderImage(), children]
    });
  }
  renderFooter() {
    const {
      includeFooter,
      moreButtons,
      extraFooter,
      extraRightAlignedFooter,
      footerAlign,
      showExtraFooterFirst,
      use
    } = this.props;
    const confirmButton = this.renderConfirmButton();
    const cancelButton = this.renderCancelButton();
    const footerClasses = classNames(showExtraFooterFirst && 'p-top-4', use === 'embedded' && 'flex-shrink-0');
    if (!includeFooter) {
      return null;
    }
    if (!(confirmButton || cancelButton || moreButtons || extraFooter)) {
      return null;
    }
    let Component = UIDialogFooter;
    if (use === 'panel') {
      Component = UIPanelFooter;
    }
    return /*#__PURE__*/_jsx(Component, {
      align: footerAlign,
      className: footerClasses,
      children: /*#__PURE__*/_jsxs(UIMedia, {
        align: "center",
        children: [/*#__PURE__*/_jsxs(UIMediaBody, {
          children: [showExtraFooterFirst && /*#__PURE__*/_jsx("div", {
            className: "p-left-1",
            children: extraFooter
          }), /*#__PURE__*/_jsxs(UIList, {
            inline: true,
            className: "display-flex flex-wrap",
            childClassName: "p-bottom-1",
            children: [confirmButton, moreButtons, cancelButton, !showExtraFooterFirst && extraFooter]
          })]
        }), extraRightAlignedFooter && /*#__PURE__*/_jsx(UIMediaRight, {
          children: extraRightAlignedFooter
        })]
      })
    });
  }
  renderConfirmButton() {
    let style;
    let component;
    const {
      onConfirm,
      confirmHref,
      use,
      confirmUse,
      confirmDisabled,
      confirmLabel,
      confirmDisabledTooltip,
      confirmTarget,
      showConfirmButton,
      isConfirmButtonLoading,
      confirmProps
    } = this.props;
    if (!showConfirmButton) {
      return null;
    }
    if (use === 'sidebar') {
      style = SIDEBAR_BUTTON_STYLE;
    }
    const ButtonComponent = isConfirmButtonLoading ? UILoadingButton : UIButton;
    const button = /*#__PURE__*/_jsx(ButtonComponent, Object.assign({
      onClick: onConfirm,
      style: style,
      href: confirmHref,
      target: confirmTarget,
      use: confirmUse,
      disabled: confirmDisabled,
      "data-selenium-test": "base-dialog-confirm-btn",
      "data-confirm-button": "accept"
    }, confirmProps, {
      children: confirmLabel || I18n.text('customerDataUiUtilities.BaseDialog.save')
    }));
    if (confirmDisabledTooltip && confirmDisabled) {
      component = /*#__PURE__*/_jsx(UITooltip, {
        title: confirmDisabledTooltip,
        children: button
      });
    } else {
      component = button;
    }
    return component;
  }
  renderCancelButton() {
    let style;
    const {
      onReject,
      cancelHref,
      use,
      rejectLabel,
      cancelTarget
    } = this.props;
    if (!this.props.showCancelButton) {
      return null;
    }
    let clickHandler = {
      onClick: onReject
    };
    if (cancelHref) {
      clickHandler = {};
    }
    if (use === 'sidebar') {
      style = SIDEBAR_BUTTON_STYLE;
    }
    return /*#__PURE__*/_jsx(UIButton, Object.assign({}, clickHandler, {
      "data-selenium-test": "base-dialog-cancel-button",
      use: "secondary",
      style: style,
      href: cancelHref,
      target: cancelTarget,
      onClick: onReject,
      children: rejectLabel || I18n.text('customerDataUiUtilities.BaseDialog.cancel')
    }));
  }
  renderHeaderImage() {
    const {
      headerImage,
      headerImageOffsetTop,
      headerImageOffsetBottom
    } = this.props;
    if (!headerImage) {
      return null;
    }
    return /*#__PURE__*/_jsx(UIDialogHeaderImage, {
      offsetTop: headerImageOffsetTop,
      offsetBottom: headerImageOffsetBottom,
      children: /*#__PURE__*/_jsx(UIImage, {
        src: headerImage,
        responsive: false
      })
    });
  }
  render() {
    let style;
    const {
      width,
      className,
      showCloseButton,
      onReject,
      use,
      dialogClassName,
      offsetTop,
      size,
      onOpenComplete,
      onScroll,
      panelClassName,
      onCloseComplete
    } = this.props;
    let extraProps;
    let Component;
    if (use === 'sidebar') {
      Component = UIModalPanel;
      extraProps = {
        onEsc: showCloseButton ? onReject : undefined,
        onOpenComplete,
        onCloseComplete
      };
    } else if (use === 'panel') {
      Component = UIPanel;
      extraProps = {
        panelClassName
      };
    } else if (use === 'embedded') {
      Component = EmbeddedComponent;
    } else {
      Component = UIModal;
      extraProps = {
        onEsc: showCloseButton ? onReject : undefined,
        onOpenComplete,
        onCloseComplete
      };
    }
    if (offsetTop) {
      style = {
        marginTop: `${offsetTop}px`
      };
    }
    return /*#__PURE__*/_jsxs(Component, Object.assign({
      className: classNames('base-dialog', className),
      "data-selenium-test": this.props['data-selenium-test'],
      width: width,
      use: use,
      size: size,
      style: style,
      dialogClassName: dialogClassName,
      onScroll: onScroll
    }, extraProps, {
      children: [this.renderHeader(), this.renderBody(), this.renderFooter()]
    }));
  }
}
BaseDialog.propTypes = propTypes;
BaseDialog.defaultProps = defaultProps;
export default BaseDialog;